import { useState, useMemo } from "react";
import { TableLoaderRow } from "@/components/ui/loader";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked, canExportContractReports } from "@/lib/roles";
import { ListExportButtons } from "@/features/reports/ListExportButtons";
import { ContractKpis, type ContractKpiKey } from "@/features/contracts/ContractKpis";
import { ContractStatusBadge, ContractTypeBadge, DaysToExpiry, formatContractTypeLabel } from "@/features/contracts/badges";
import { useContracts, useRunExpiryCheck, useVendorsList } from "@/features/contracts/api";
import { CONTRACT_STATUSES, CONTRACT_TYPES, type ContractStatus, type ContractType } from "@/features/contracts/types";
import { WarrantyExpiringWidget } from "@/features/contracts/WarrantyExpiringWidget";
import { formatDate } from "@/features/assets/format";
import { useConfigOptions } from "@/features/configuration/api";


export const Route = createFileRoute("/_authenticated/contracts/")({
  component: ContractsListPage,
  validateSearch: (s: Record<string, unknown>) => ({ expiry: s.expiry as string | undefined }),
});

const VIEWER_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "finance", "purchase_stores", "management"]);
const EDITOR_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);
const ALERT_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);

function ContractsListPage() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const roles = me?.roles ?? [];
  const canView = roles.some((r) => VIEWER_ROLES.has(r));
  const canEdit = roles.some((r) => EDITOR_ROLES.has(r));
  const canRunAlerts = roles.some((r) => ALERT_ROLES.has(r));
  const locked = isBranchLocked(roles);

  const [search, setSearch] = useState("");
  const [type, setType] = useState<string>("all");
  const [vendorId, setVendorId] = useState<string>("all");
  const { expiry } = Route.useSearch();
  const [status, setStatus] = useState<string>("all");
  const [expiring, setExpiring] = useState<string>(expiry === "soon" ? "30" : "all");
  const [kpiFilter, setKpiFilter] = useState<ContractKpiKey | null>(null);

  const handleKpiSelect = (k: ContractKpiKey | null) => {
    setKpiFilter(k);
    if (k === "active") { setStatus("Active"); setExpiring("all"); }
    else if (k === "expiring") { setStatus("Active"); setExpiring("30"); }
    else if (k === "expired") { setStatus("Expired"); setExpiring("all"); }
    else { setStatus("all"); setExpiring("all"); }
  };

  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;

  const { data: vendors = [] } = useVendorsList();
  const { data: rows = [], isLoading } = useContracts({
    search,
    type: type === "all" ? null : (type as ContractType),
    vendorId: vendorId === "all" ? null : vendorId,
    status: status === "all" ? null : (status as ContractStatus),
    branchId: effectiveBranchId,
    expiringWithinDays: expiring === "all" ? null : Number(expiring),
  });
  const runAlerts = useRunExpiryCheck();

  const { data: typeCfg } = useConfigOptions("contracts", "contract_type");
  const { data: statusCfg } = useConfigOptions("contracts", "contract_status");

  const typeChoices = useMemo(() => {
    const fromCfg = (typeCfg ?? [])
      .filter((o) => o.is_active)
      .map((o) => ({
        value: o.value || o.option_key || o.label,
        label: o.label ? formatContractTypeLabel(o.label) : formatContractTypeLabel(o.value || o.option_key || ""),
      }));
    if (fromCfg.length > 0) return fromCfg;
    return (CONTRACT_TYPES as readonly string[]).map((t) => ({
      value: t,
      label: formatContractTypeLabel(t),
    }));
  }, [typeCfg]);

  const statusChoices = useMemo(() => {
    const fromCfg = (statusCfg ?? [])
      .filter((o) => o.is_active)
      .map((o) => ({
        value: o.value || o.label,
        label: o.label || o.value,
      }));
    if (fromCfg.length > 0) return fromCfg;
    return (CONTRACT_STATUSES as readonly string[]).map((s) => ({ value: s, label: s }));
  }, [statusCfg]);


  if (!me) return null;
  if (!canView) {
    return (
      <Card className="mx-auto max-w-xl"><CardContent className="space-y-2 p-6">
        <h2 className="text-lg font-semibold">Permission required</h2>
        <p className="text-sm text-muted-foreground">You do not have access to the Contracts module.</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contracts</h1> <ReadOnlyPill module="contracts" />
          <p className="text-sm text-muted-foreground">Warranty · Extended Warranty · AMC · CMC</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ListExportButtons
            rows={() =>
              rows.map((c) => ({
                Contract: c.contract_no,
                Type: c.type,
                Vendor: c.vendor?.name ?? "",
                Assets: c.assets_count ?? 0,
                "Start Date": c.start_date ?? "",
                "End Date": c.end_date ?? "",
                Value: c.contract_value ?? "",
                "PM Visits Done": c.pm_visits_done ?? 0,
                "PM Visits Planned": c.pm_visits_included ?? "",
                "Response SLA (h)": c.response_sla_hours ?? "",
                "Resolution SLA (h)": c.resolution_sla_hours ?? "",
                Status: c.status,
              }))
            }
            filename="Contract_Register"
            sheetName="Contracts"
            disabled={isLoading || rows.length === 0}
            canExport={canExportContractReports(roles)}
          />
          {canRunAlerts && (
            <Button
              variant="outline"
              disabled={runAlerts.isPending}
              onClick={async () => {
                try {
                  const res = await runAlerts.mutateAsync();
                  if (res.notifications_created > 0 || res.expired_count > 0) {
                    toast.success(
                      `Expiry check complete · ${res.expired_count} contract(s) marked Expired, ${res.expiring_count} expiring soon (${res.notifications_created} alert notification(s) generated)`
                    );
                  } else if (res.expiring_count > 0) {
                    toast.info(
                      `Expiry check complete · ${res.expiring_count} contract(s) expiring within 30 days (alerts already up-to-date)`
                    );
                  } else {
                    toast.success("Expiry check complete · All active contracts are healthy and up to date.");
                  }
                } catch (e) {
                  toast.error((e as Error).message || "Failed to check contract expiry alerts");
                }
              }}
            >
              {runAlerts.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Bell className="mr-1 h-4 w-4" />
              )}
              Check Contract Expiry Alerts
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => navigate({ to: "/contracts/new" })}>
              <Plus className="mr-1 h-4 w-4" />New Contract
            </Button>
          )}
        </div>
      </div>

      <ContractKpis activeKey={kpiFilter} onSelect={handleKpiSelect} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          <Card>
            <CardContent className="flex flex-wrap items-center gap-2 p-3">
              <Input placeholder="Search number or vendor…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" />
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {typeChoices.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger className="w-40"><SelectValue placeholder="Vendor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statusChoices.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Branch is governed by the top-bar branch switcher (BranchContext). */}
              {locked && (
                <span className="text-xs text-muted-foreground">
                  Branch-locked to your assigned branch.
                </span>
              )}

              <Select value={expiring} onValueChange={setExpiring}>
                <SelectTrigger className="w-44"><SelectValue placeholder="Expiry within" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any expiry</SelectItem>
                  <SelectItem value="7">≤ 7 days</SelectItem>
                  <SelectItem value="30">≤ 30 days</SelectItem>
                  <SelectItem value="60">≤ 60 days</SelectItem>
                  <SelectItem value="90">≤ 90 days</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contract</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-center">Assets</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead className="text-right">Value (₹)</TableHead>
                    <TableHead className="text-center">PM Visits</TableHead>
                    <TableHead>SLA (R / Res)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Days</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableLoaderRow colSpan={11} />}
                  {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={11} className="py-8 text-center text-muted-foreground">No contracts match your filters.</TableCell></TableRow>}
                  {rows.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate({ to: "/contracts/$contractId", params: { contractId: c.id } })}>
                      <TableCell className="font-mono text-xs">
                        <Link to="/contracts/$contractId" params={{ contractId: c.id }} className="hover:underline">{c.contract_no}</Link>
                      </TableCell>
                      <TableCell><ContractTypeBadge value={c.type} /></TableCell>
                      <TableCell>{c.vendor?.name ?? "—"}</TableCell>
                      <TableCell className="text-center">{c.assets_count ?? 0}</TableCell>
                      <TableCell>{formatDate(c.start_date)}</TableCell>
                      <TableCell>{formatDate(c.end_date)}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.contract_value ? new Intl.NumberFormat("en-IN").format(c.contract_value) : "—"}</TableCell>
                      <TableCell className="text-center text-xs">{c.pm_visits_done}/{c.pm_visits_included ?? "—"}</TableCell>
                      <TableCell className="text-xs">{c.response_sla_hours ?? "—"}h / {c.resolution_sla_hours ?? "—"}h</TableCell>
                      <TableCell><ContractStatusBadge value={c.status} /></TableCell>
                      <TableCell><DaysToExpiry endDate={c.end_date} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3">
          <WarrantyExpiringWidget />
        </div>
      </div>
    </div>
  );
}
